import axios from "axios";
// import { Http } from './http.init';
import { ResponseWrapper, ErrorWrapper } from "./util";
import $store from "../store";
import $router from "../router";

const API_URL = process.env.VUE_APP_API_URL;

export class AuthService {
  /**
   * Create a new account
   * @param {Object} data - The account creation payload
   * @returns {ResponseWrapper} - The response data wrapper
   * @throws {ErrorWrapper} - The error data wrapper
   */
  static async makeCreateAccount(data) {
    const response = await axios.post(`${API_URL}users`, data);
    return new ResponseWrapper(response, response.data.data);
  }

  /**
   * Login a user
   * @param {Object} credentials - The login payload containing email and password
   * @returns {boolean} - Returns true on successful login
   * @throws {ErrorWrapper} - The error data wrapper
   */
  static async makeLogin({ email, password }) {
    const response = await axios.post(`${API_URL}login`, { email, password });
    return new ResponseWrapper(response, response?.data?.data);
  }

  /**
   * Verify OTP for MFA login
   * @param {Object} data - { email, otp }
   * @returns {ResponseWrapper}
   */
  static async verifyOtp({ email, otp }) {
    const response = await axios.post(`${API_URL}verify-otp`, { email, otp });
    return new ResponseWrapper(response, response?.data?.data);
  }

  /**
   * Resend OTP
   * @param {Object} data - { email }
   * @returns {ResponseWrapper}
   */
  static async resendOTP({ email }) {
    const response = await axios.post(`${API_URL}send-otp`, { email });
    return new ResponseWrapper(response, response?.data?.data);
  }

  static async resendLoginOTP({ email }) {
    const response = await axios.post(`${API_URL}send-login-otp`, { email });
    return new ResponseWrapper(response, response?.data?.data);
  }

  /**
   * Verify login OTP (MFA)
   */
  static async verifyLoginOtp({ email, otp }) {
    const response = await axios.post(`${API_URL}verify-login-otp`, {
      email,
      otp,
    });

    // backend returns full response → keep same wrapper behavior
    return new ResponseWrapper(response, response?.data?.data);
  }

  /**
   * Logout the current user
   * @returns {ResponseWrapper} - The response data wrapper
   * @throws {ErrorWrapper} - The error data wrapper
   */
  static async makeLogout() {
    try {
      const response = await axios.post(
        `${API_URL}logout`,
        {},
        {
          headers: { Authorization: AuthService.getBearer() },
        }
      );

      // Remove local data
      AuthService.removeToken();

      return new ResponseWrapper(response, response?.data?.data);
    } catch (error) {
      // Still remove token, but throw error for UI handling
      AuthService.removeToken();
      throw new ErrorWrapper(error);
    }
  }

  /**
   * Fetch the current user's profile
   * @returns {ResponseWrapper} - The response data wrapper
   * @throws {ErrorWrapper} - The error data wrapper
   */
  static async getProfile() {
    try {
      const response = await axios.get(`${API_URL}get-profile`, {
        headers: { Authorization: AuthService.getBearer() },
      });
      $store.commit("user/SET_CURRENT_USER", response?.data?.data);
      return new ResponseWrapper(response, response?.data?.data);
    } catch (error) {
      $router.push({ name: "login" }).catch(() => {});
      throw new ErrorWrapper(error);
    }
  }

  /**
   * Remove the current user's token
   * @returns {boolean} - True if the token was successfully removed
   */

  static removeToken() {
    // Remove authentication details
    localStorage.removeItem("token");
    localStorage.removeItem("ID");

    // Reset Vuex user state
    $store.commit("user/SET_CURRENT_USER", {});

    return Boolean(localStorage.removeItem("token"));
  }

  /**
   * Check if the current user has a token
   * @returns {string|null} - The token if it exists, otherwise null
   */
  static hasToken() {
    return localStorage.getItem("token");
  }

  /**
   * Get the current Bearer token
   * @returns {string} - The Bearer token
   */
  static getBearer() {
    return `Bearer ${localStorage.getItem("token")}`;
  }

  /**
   * Update active hospital for super-admin
   * @param {string} hospitalId
   * @returns {ResponseWrapper}
   */
  static async updateActiveHospital(hospitalId) {
    const response = await axios.post(
      `${API_URL}admin/super-admin/active-hospital`,
      { hospitalId },
      {
        headers: { Authorization: AuthService.getBearer() },
      }
    );
    return new ResponseWrapper(response, response?.data?.data);
  }

  /**
   * Fetch hospitals list for super-admin
   * @returns {ResponseWrapper}
   */
  static async getSuperAdminHospitals() {
    const response = await axios.get(`${API_URL}admin/super-admin/hospitals`, {
      headers: { Authorization: AuthService.getBearer() },
    });
    return new ResponseWrapper(response, response?.data?.data);
  }

  /**
   * Update same-day appointments setting for a hospital
   * @param {string} hospitalId
   * @param {boolean} sameDayAppointments
   * @returns {ResponseWrapper}
   */
  static async updateSameDayAppointments(hospitalId, sameDayAppointments) {
    const response = await axios.patch(
      `${API_URL}admin/hospitals/${hospitalId}/same-day-appointments`,
      { sameDayAppointments },
      {
        headers: { Authorization: AuthService.getBearer() },
      }
    );
    return new ResponseWrapper(response, response?.data?.data);
  }
}
